const wikiPhoto = (file) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=1600`;

const googleMaps = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const osmEmbed = (lat, lon, delta = 0.018) => {
  const minLon = lon - delta;
  const minLat = lat - delta * 0.65;
  const maxLon = lon + delta;
  const maxLat = lat + delta * 0.65;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon},${minLat},${maxLon},${maxLat}&layer=mapnik&marker=${lat},${lon}`;
};

const TRAILHEAD_QUERY =
  'Rocks and Roots Trail Trailhead, 3993 Hollenback Rd, Lewis Center, OH 43035';

const PHOTOS = {
  lake: wikiPhoto('Alum Creek Lake 1.jpg'),
  woods: wikiPhoto('Alum Creek State Park in November 1.jpg'),
  dam: wikiPhoto('Alum Creek Dam 1.jpg'),
};

/**
 * Friends-group morning plan for Rocks and Roots Trail at Alum Creek State Park.
 */
export const ROCKS_AND_ROOTS_MORNING_HIKE_POST = {
  id: 'featured-rocks-and-roots-morning-hike',
  slug: 'rocks-and-roots-alum-creek-morning-hike-plan',
  title: 'Rocks and Roots Morning Hike Plan (Alum Creek, 7:00 a.m.)',
  excerpt:
    'Share this with the group: meet at the Hollenback Road trailhead at 7:00 a.m., hike the North Loop (or a shorter turnaround), what to pack, where not to park, and how long we will actually be out.',
  publishedAt: '2026-09-06T22:00:00.000Z',
  coverImageUrl: PHOTOS.woods,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'panel',
      title: 'Update: we hiked it',
      paragraphs: [
        'The group met at **7:00 a.m.** and was back at the lot by **9:30 a.m.** Photos and how the morning went: [Rocks and Roots recap](/blog/rocks-and-roots-alum-creek-morning-hike-recap).',
      ],
    },
    {
      type: 'stats',
      ariaLabel: 'Morning hike snapshot',
      items: [
        { value: '7:00 a.m.', label: 'Meet at the trailhead' },
        { value: '~6 mi', label: 'North Loop (our default)' },
        { value: '2–2.5 hr', label: 'Hiking time on the full loop' },
        { value: 'Moderate', label: 'Roots & mud — not a big climb' },
      ],
    },
    {
      type: 'panel',
      title: 'The plan in one paragraph',
      paragraphs: [
        'We are hiking **Rocks and Roots** at **Alum Creek State Park** — the closest moderate dirt trail to Powell, Polaris, Lewis Center, and Delaware. Meet **7:00 a.m.** at the **Rocks and Roots trailhead on Hollenback Road** (3993 Hollenback Rd, Lewis Center). Default route: **North Loop**, about **6 miles**, mostly woods, lake views on one side, **2 to 2.5 hours** of hiking. Anyone who wants a shorter morning turns around with a buddy at **45–60 minutes**. Cars back at the lot about **9:15–9:45 a.m.** Coffee after.',
        'This is moderate because of **rocks, roots, and mud**, not because of a Cemetery Ridge–style climb. If it poured yesterday, we postpone — wet clay on this trail is miserable and tears up the path.',
      ],
      chips: ['Alum Creek State Park', 'Lewis Center, OH', 'Friends group', '7:00 a.m. start'],
    },
    {
      type: 'table',
      title: 'Meet here — pin this, not the beach',
      headers: ['Item', 'Details'],
      rows: [
        ['Trail', 'Rocks and Roots (hiking / trail-running singletrack)'],
        ['Park', 'Alum Creek State Park'],
        ['Meet', '7:00 a.m. at the trailhead parking lot — be in the lot by 6:50 if you can'],
        [
          'Address to drop in Maps',
          `3993 Hollenback Rd, Lewis Center, OH 43035 · [Google Maps](${googleMaps(TRAILHEAD_QUERY)})`,
        ],
        ['Landmark', 'Same road as the marina and the dog park — look for Rocks and Roots trailhead parking, not Gnomewood / Phase 1 bike lots'],
        ['Do not navigate to', 'Beach (3400 Lewis Center Rd) or COMBO MTB lot (6914 Lewis Center Rd)'],
        ['From Polaris', '~12–15 min: Polaris Pkwy west → S Old State Rd north → right on Hollenback'],
        ['From Powell / Dublin', '~15–20 min via US 23 or Sawmill / Lewis Center Rd to Hollenback'],
        ['Park hours', 'Sunrise–sunset (free). Early September sunrise is right around 7:00 a.m. — bring a small light for the lot'],
        ['Park office', '(740) 548-4631 · weekdays 8 a.m.–4:30 p.m.'],
      ],
    },
    {
      type: 'mapEmbed',
      title: 'Trailhead map',
      intro:
        'Hollenback Road, west side of the lake. Screenshot this pin overnight — cell service is uneven in the woods.',
      maps: [
        {
          title: 'Rocks and Roots trailhead',
          address: '3993 Hollenback Rd, Lewis Center, OH 43035',
          embedUrl: osmEmbed(40.1985, -82.9575, 0.016),
          directionsUrl: googleMaps(TRAILHEAD_QUERY),
        },
      ],
    },
    {
      type: 'imageGallery',
      title: 'What we are walking through',
      intro:
        'Wooded reservoir shoreline — not a gorge. Photos: Wikimedia Commons (Alum Creek State Park).',
      images: [
        {
          url: PHOTOS.woods,
          alt: 'Wooded shoreline at Alum Creek State Park',
          caption: 'Typical woods along the reservoir — roots, shade, and mud after rain.',
          mapUrl: googleMaps(TRAILHEAD_QUERY),
        },
        {
          url: PHOTOS.lake,
          alt: 'Alum Creek Lake',
          caption: 'Lake views on the eastern side of the North Loop.',
          mapUrl: googleMaps(TRAILHEAD_QUERY),
        },
        {
          url: PHOTOS.dam,
          alt: 'Alum Creek Dam',
          caption: 'Dam is nearby if anyone wants an easy paved add-on after — not our 7 a.m. route.',
          mapUrl: googleMaps('Alum Creek Dam, Lewis Center, OH'),
        },
      ],
    },
    {
      type: 'panel',
      title: '7:00 a.m. timeline',
      orderedList: [
        '**Night before:** Check radar. If the trail is soaked, we cancel in the group chat by 9 p.m. Pack shoes, 1 L water, and a headlamp/phone light for the parking lot.',
        '**6:35–6:45 a.m.:** Leave Powell / Polaris / Dublin. One shared Maps pin: **3993 Hollenback Rd**.',
        '**6:50 a.m.:** First cars in the lot. Restroom if you need one (vault toilets / nearby day-use — do not count on a flush cafe).',
        '**7:00 a.m.:** Group photo, quick brief (North Loop, turnaround rule), on trail.',
        '**7:00–8:00 a.m.:** First hour — slow on roots, talk, sip water. Short-hike people turn around here with a buddy and wait at the cars.',
        '**~9:15–9:45 a.m.:** Full-loop group back at the lot. Stretch, then breakfast in Polaris / Powell if people want.',
      ],
    },
    {
      type: 'table',
      title: 'Pick a distance before we leave the lot',
      headers: ['Option', 'Distance / time', 'Who it is for'],
      rows: [
        [
          'Short morning (recommended bailout)',
          '45–60 min out, then back the same way (~2–3 mi)',
          'Anyone unsure, recovering, or who has to leave by 8:30. Always turn around with a partner.',
        ],
        [
          'North Loop (our default)',
          '~6.1–6.7 mi loop · ~2–2.5 hr hike · ~200 ft gain',
          'The friends-group plan. Orange-blazed woods loop, lake views, clockwise is common.',
        ],
        [
          'South Loop',
          '~6.7 mi loop · similar time · a bit more technical',
          'Only if the whole group already knows North Loop and wants the harder figure-eight half.',
        ],
        [
          'Both loops (no)',
          '~12+ mi / 10K + 10K',
          'Not this morning. That is a trail-running day, not a 7 a.m. friends hike.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'What “moderate” means here',
      paragraphs: [
        'ODNR lists Rocks and Roots as **4.1 miles, moderate**. The popular runner-built trail is a **figure-eight**: North and South loops of about **6 miles each**, starting at the same Hollenback trailhead. We are doing **one loop** (North).',
        'Expect a winding dirt singletrack with **rocks, roots, downed logs, and mud**. Elevation gain is modest (about 200 feet) — nothing like Cemetery Ridge’s zigzag. The work is **footing**. Slow down. Nobody is racing.',
        'Hikers, runners, and leashed dogs share the trail. Yield politely. Stay off the **mountain-bike** system on Lewis Center / Africa Road.',
      ],
    },
    {
      type: 'panel',
      title: 'Pack list (screenshot this)',
      list: [
        '**Shoes with tread** — trail runners or hiking shoes. The name of the trail is the warning.',
        '**1 liter of water** minimum (more if it is warm). No store on Hollenback.',
        '**Small snack** (bar or banana) for after the loop.',
        '**Tick spray** on skin and socks — woods and grass, spring through fall.',
        '**Light layer** — 7 a.m. is cool; you will warm up in 15 minutes.',
        '**Phone + offline screenshot** of this page and the Maps pin.',
        '**Headlamp or phone flashlight** for the lot at 6:50 a.m.',
        '**Group kit (one person):** blister bandage, extra water, car key plan.',
      ],
    },
    {
      type: 'panel',
      title: 'Weather, mud, and cancel rules',
      list: [
        'Thunder or a soaked trail = we move the date. Rocks and Roots after heavy rain is a slip-and-rut hike.',
        'Light dew is fine. Standing mud for a mile is not.',
        'If someone is late past **7:10 a.m.**, we start and they catch the short out-and-back or meet us at the cars.',
        'Turnaround is a group decision on the short option; on the full North Loop we stay together unless a pair explicitly splits for the bailout.',
        'Leashed dogs only if the owner already hikes this trail — first-time dogs on roots slow everyone down. Confirm in the chat.',
      ],
    },
    {
      type: 'panel',
      title: 'Paste this in the group chat',
      paragraphs: [
        'Rocks and Roots · Alum Creek. **Meet 7:00 a.m.** at **3993 Hollenback Rd, Lewis Center, OH 43035** (Rocks and Roots trailhead — not the beach, not the bike park). Default: **North Loop ~6 miles, 2–2.5 hours**. Shorter option: turn around at 45–60 min with a buddy. Closed-toe shoes, 1L water, tick spray, light for the lot. If it poured last night we cancel. Plan: https://www.dailysportslife.com/blog/rocks-and-roots-alum-creek-morning-hike-plan',
      ],
    },
  ],
  relatedPostsTitle: 'More Alum Creek & Ohio trail guides',
  relatedPosts: [
    {
      slug: 'rocks-and-roots-alum-creek-morning-hike-recap',
      title: 'Rocks and Roots Recap: Alum Creek Morning Hike (7:00–9:30 a.m.)',
      blurb: 'we did it — crew photo and how the 7:00 a.m. plan actually went.',
    },
    {
      slug: 'alum-creek-best-hike-spots-beginner-moderate',
      title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
      blurb: 'easier trails at the same park if someone wants Storybook or Hollenback instead.',
    },
    {
      slug: 'cemetery-ridge-clear-creek-hike-prep',
      title: 'How to Prepare for Cemetery Ridge at Clear Creek Metro Park',
      blurb: 'a steeper next-step hike when this loop feels easy.',
    },
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'first-hike options closer to paved paths.',
    },
  ],
  referenceLinks: [
    {
      label: 'Ohio DNR — Alum Creek State Park (hours, Rocks and Roots on the hiking list)',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/alum-creek-state-park',
    },
    {
      label: 'Ohio DNR — Alum Creek hiking map (PDF)',
      url: 'https://dam.assets.ohio.gov/image/upload/ohiodnr.gov/documents/parks/parkmaps/alumcreektrailmap.pdf',
    },
    {
      label: 'Google Maps — Rocks and Roots trailhead (3993 Hollenback Rd)',
      url: googleMaps(TRAILHEAD_QUERY),
    },
    {
      label: 'Best Alum Creek hike spots — beginner to moderate',
      url: '/blog/alum-creek-best-hike-spots-beginner-moderate',
    },
  ],
};
